import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { StickyRowTheme } from '../../../theme/default-theme';
import { ProductCarousel, IProductCarousel } from '../product-carousel/ProductCarousel';
import { ProductBriefCollectionWrapper } from '../product-brief-collection/ProductBriefCollectionWrapper';
import { IProductBriefCollection } from '../product-brief-collection/ProductBriefCollection';
import { DEFAULT_GRID_BREAKPOINTS } from '../../constants/ProductGrid';
import { ICTAConfig } from '../../../exported/product-grid-wrapper/ProductGridWrapper';
import { ProductGridFilters } from './ProductGridFilters';
import { ContentSpacing, Breakpoints } from 'elc-base-theme';
import { Heading4 } from 'elc-typography';
import { classes } from '../../../internal/utils/Classes';
import { ProductSortingDropdown } from '../../../internal/views/product-grid/ProductSortingDropdown';
import { Row } from 'elc-grid';
import { TOGGLE_TYPE } from '../../constants/AutoReplenish';
import { FilterSet } from '../../domain/entities/FilterSet';
import { Desktop, Mobile } from 'elc-layouts';
import { FILTER_TYPES } from '../product-filters/FiltersConfig';

export enum Formatter {
    DEFAULT = 'default',
    CAROUSEL = 'carousel'
}

export interface IProductGridProps {
    formatter: Formatter;
    carouselProps: IProductCarousel['config'];
    briefProps: IProductBriefCollection['config'];
    productSortingDropdownValue: string;
    productsNumber: number;
    appliedFilters?: FilterSet[];
    filterSets?: FilterSet[];
    isAllGiftsFilterApplied?: boolean;
    areFiltersApplied?: boolean;
    productGridCTAConfig: ICTAConfig;
    positionGridInsideAccordion?: boolean;
    enableFilters?: boolean;
    totalAppliedFilters?: number;
    hideSorting?: boolean;
    isFilterDisplayStacked?: boolean;
    isFilterDisplayTabbed?: boolean;
    className?: string;
    filtersDisplayType?: FILTER_TYPES;
    setProductSortingDropdownValue(_: string, v: string): void;
}

export const QUICK_VIEW_SELECTOR_CLASSNAME = 'elc-quickview-renderer';

const ProductCTAWrapper = styled.div`
    margin-top: ${ContentSpacing.space24};
`;

const ProductCTA = styled.a`
    width: ${ContentSpacing.space60};
`;

const StickyRow = styled(Row)`
    display: flex;
    justify-content: space-between;
    user-select: none;
    ${StickyRowTheme};
`;

@observer
class ProductGrid extends React.Component<IProductGridProps> {
    public static defaultProps: IProductGridProps;

    private getProductCTA = (productGridCTAConfig: ICTAConfig) => {
        const productCTAWrapperClassNames = classes('product-cta-wrapper');
        const productCTAClassNames = classes('product-cta');
        const { label, absoluteUrl, url, newTab } = productGridCTAConfig;

        let ctaURL = url;
        if (absoluteUrl && url) {
            const isAbsoluteUrlRegex = new RegExp('^(?:[a-z]+:)?//', 'i');
            const baseUrl = isAbsoluteUrlRegex.test(url) ? '' : 'https://';
            ctaURL = `${baseUrl}${url}`;
        }

        const ctaUrlTarget = newTab ? '_blank' : '';

        return (
            <ProductCTAWrapper className={productCTAWrapperClassNames}>
                <ProductCTA className={productCTAClassNames} href={ctaURL} target={ctaUrlTarget}>
                    <Heading4>{label}</Heading4>
                </ProductCTA>
            </ProductCTAWrapper>
        );
    };

    private getSorting(displaySortingDropdown: boolean) {
        if (!displaySortingDropdown) {
            return null;
        }

        const sortWrapperClassNames = classes('sort-wrapper');

        return (
            <ProductSortingDropdown
                className={sortWrapperClassNames}
                productSortingDropdownValue={this.props.productSortingDropdownValue}
                setProductSortingDropdownValue={this.props.setProductSortingDropdownValue}
            />
        );
    }

    private getFilterBarContent(wrapperClassName: string) {
        const displaySortingDropdown = !this.props.hideSorting && this.props.productsNumber > 0;

        return (
            <StickyRow className={wrapperClassName}>
                {this.props.enableFilters && (
                    <ProductGridFilters
                        productsNumber={this.props.productsNumber}
                        filterSets={this.props.filterSets || []}
                        appliedFilters={this.props.appliedFilters || []}
                        totalAppliedFilters={this.props.totalAppliedFilters}
                        filtersDisplayType={this.props.filtersDisplayType}
                    />
                )}

                {this.getSorting(displaySortingDropdown)}
            </StickyRow>
        );
    }

    private getFilterBar() {
        const { filtersDisplayType } = this.props;
        const filterClassName = `elc-styled-filters elc-styled-${filtersDisplayType}-filters`;

        return (
            <>
                <Desktop minWidth={Breakpoints.desktop}>
                    {() => this.getFilterBarContent(filterClassName)}
                </Desktop>
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() => this.getFilterBarContent('elc-styled-filters elc-mobile-filters')}
                </Mobile>
            </>
        );
    }

    public render() {
        const {
            carouselProps,
            briefProps,
            isAllGiftsFilterApplied,
            areFiltersApplied,
            formatter,
            productGridCTAConfig,
            positionGridInsideAccordion
        } = this.props;

        const productGridCTA =
            productGridCTAConfig.enabled && this.getProductCTA(productGridCTAConfig);

        if (formatter === Formatter.CAROUSEL) {
            return (
                <>
                    <ProductCarousel config={carouselProps} />
                    {productGridCTA}
                </>
            );
        }

        return (
            <>
                {this.getFilterBar()}
                <ProductBriefCollectionWrapper
                    isAllGiftsFilterApplied={isAllGiftsFilterApplied}
                    config={{ ...briefProps, positionGridInsideAccordion }}
                    areFiltersApplied={areFiltersApplied}
                />
                <div className={QUICK_VIEW_SELECTOR_CLASSNAME} />
                {productGridCTA}
            </>
        );
    }
}

ProductGrid.defaultProps = {
    carouselProps: {
        products: [],
        hideProductPrice: false,
        hideProductPriceRange: true,
        hideProductBadge: false,
        showSubDisplayName: false,
        hideProductRating: false,
        enableNotifyMe: false,
        enableQuickView: false,
        enableMobileQuickView: false,
        hideReviewsNumber: false,
        hideReadReviewsLink: true,
        enableShadePicker: true,
        enableSizePicker: true,
        shortDescriptionLocation: {
            product_full: true,
            product_brief: true,
            quickshop: true
        },
        enableProductDetailLink: true,
        autoReplenishOptions: [],
        autoReplenishToggleType: TOGGLE_TYPE.CHECKBOX
    },
    briefProps: {
        gridItems: [],
        defaultBreakpoints: DEFAULT_GRID_BREAKPOINTS,
        breakpoints: DEFAULT_GRID_BREAKPOINTS,
        isLoaded: false,
        autoReplenishOptions: [],
        autoReplenishToggleType: TOGGLE_TYPE.CHECKBOX
    },
    formatter: Formatter.DEFAULT,
    productGridCTAConfig: {
        enabled: false,
        label: '',
        absoluteUrl: false,
        url: '',
        newTab: false
    },
    productSortingDropdownValue: '',
    productsNumber: 9,
    setProductSortingDropdownValue: () => null
};

export default ProductGrid;
